import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  ExternalLink,
  IdCard,
  KeyRound,
  MoreHorizontal,
  Plus,
  RotateCcw,
  Search,
  Trash2,
  UserPlus,
  Users,
  UserX,
} from 'lucide-react';

import { fieldUserApi } from '@/features/auth/api';
import { useAuthStore } from '@/stores/auth-store';
import { PageHeader, StatusPill } from '@/lib/ui';
import { CredentialCard } from '../components/CredentialCard';
import { cardPath, cardUrl } from '../card-url';
import { FieldUserDetailPanel, type FieldUserRow } from '../components/FieldUserDetailPanel';

export function FieldUsersPage() {
  const queryClient = useQueryClient();
  const can = useMemo(() => {
    const p = useAuthStore.getState().tenant?.permissions ?? [];
    return (perm: string) => p.includes(perm);
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ['field-users'],
    queryFn: fieldUserApi.list,
  });

  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState<FieldUserRow | null>(null);
  const [cardFor, setCardFor] = useState<{
    employee_id: string;
    full_name: string;
    code: string;
    expires_at: string;
  } | null>(null);

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['field-users'] });

  const regenerate = useMutation({
    mutationFn: (id: string) => fieldUserApi.regenerateCard(id),
  });
  const resetPin = useMutation({
    mutationFn: (id: string) => fieldUserApi.resetPin(id),
  });
  const deactivate = useMutation({
    mutationFn: (id: string) => fieldUserApi.deactivate(id),
    onSuccess: invalidate,
  });
  const reactivate = useMutation({
    mutationFn: (id: string) => fieldUserApi.reactivate(id),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => fieldUserApi.remove(id),
    onSuccess: invalidate,
  });

  const rows = useMemo(() => {
    const all = (data ?? []) as FieldUserRow[];
    const q = search.trim().toLowerCase();
    if (!q) return all;
    return all.filter((fu) =>
      [fu.employee_id, fu.full_name, fu.phone ?? ''].join(' ').toLowerCase().includes(q),
    );
  }, [data, search]);

  return (
    <div className="page">
      <PageHeader
        title="Field users"
        icon={Users}
        subtitle="Mobile-app workers. Each has a unique employee ID and PIN-based login."
        actions={
          can('field_user.create') ? (
            <button onClick={() => setCreating(true)} className="btn-primary">
              <UserPlus className="h-4 w-4" />
              Add field user
            </button>
          ) : null
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by employee ID, name or phone…"
            className="input-search"
            aria-label="Search field users"
          />
        </div>
        {search && (
          <span className="text-xs text-muted-foreground">
            {rows.length} of {(data ?? []).length}
          </span>
        )}
      </div>

      <div className="data-table-wrapper">
        <div className="data-table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Employee ID</th>
                <th>Name</th>
                <th>Status</th>
                <th>Phone</th>
                <th>Last login</th>
                <th className="num"></th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                    {(data ?? []).length === 0
                      ? 'No field users yet — add one to issue a credential card.'
                      : 'No field users match your search.'}
                  </td>
                </tr>
              ) : (
                rows.map((fu) => (
                  <tr key={fu.id} onClick={() => setSelected(fu)} className="cursor-pointer">
                    <td className="font-mono text-xs">{fu.employee_id}</td>
                    <td className="font-medium">{fu.full_name}</td>
                    <td>
                      <StatusPill status={fu.status} />
                    </td>
                    <td className="muted">{fu.phone ?? '—'}</td>
                    <td className="muted text-xs">
                      {fu.last_login_at ? new Date(fu.last_login_at).toLocaleString() : 'Never'}
                    </td>
                    <td className="actions" onClick={(e) => e.stopPropagation()}>
                      <div className="inline-flex items-center gap-1">
                        {can('field_user.provision') && (
                          <>
                            <button
                              onClick={async () => {
                                const card = await regenerate.mutateAsync(fu.id);
                                setCardFor({
                                  employee_id: fu.employee_id,
                                  full_name: fu.full_name,
                                  code: card.code,
                                  expires_at: card.expires_at,
                                });
                              }}
                              className="btn-ghost btn-sm text-primary hover:text-primary"
                              title="Issue a new provisioning code (revokes the previous one)"
                            >
                              <IdCard className="h-4 w-4" />
                              <span className="hidden lg:inline">Generate card</span>
                            </button>
                            <Link
                              to={cardPath(fu.employee_id)}
                              className="btn-ghost btn-sm"
                              title={`View card · ${cardUrl(fu.employee_id)}`}
                            >
                              <ExternalLink className="h-4 w-4" />
                              <span className="hidden lg:inline">View card</span>
                            </Link>
                          </>
                        )}
                        {can('field_user.reset_pin') && (
                          <button
                            onClick={async () => {
                              const card = await resetPin.mutateAsync(fu.id);
                              setCardFor({
                                employee_id: fu.employee_id,
                                full_name: fu.full_name,
                                code: card.code,
                                expires_at: card.expires_at,
                              });
                              invalidate();
                            }}
                            className="btn-ghost btn-sm text-warning hover:text-warning"
                            title="Reset PIN"
                          >
                            <KeyRound className="h-4 w-4" />
                            <span className="hidden lg:inline">Reset PIN</span>
                          </button>
                        )}
                        {can('field_user.deactivate') && fu.status === 'ACTIVE' && (
                          <button
                            onClick={() => deactivate.mutate(fu.id)}
                            className="btn-ghost btn-sm text-danger hover:text-danger"
                            title="Deactivate"
                          >
                            <UserX className="h-4 w-4" />
                            <span className="hidden lg:inline">Deactivate</span>
                          </button>
                        )}
                        {can('field_user.update') && fu.status === 'SUSPENDED' && (
                          <button
                            onClick={() => reactivate.mutate(fu.id)}
                            className="btn-ghost btn-sm text-success hover:text-success"
                            title="Reactivate"
                          >
                            <RotateCcw className="h-4 w-4" />
                            <span className="hidden lg:inline">Reactivate</span>
                          </button>
                        )}
                        {can('field_user.delete') && (
                          <RowMenu
                            onDelete={() => {
                              if (
                                window.confirm(
                                  `Delete ${fu.full_name} (${fu.employee_id})? Their mobile access is revoked; historical data is kept.`,
                                )
                              ) {
                                remove.mutate(fu.id);
                              }
                            }}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {creating && (
        <AddFieldUserModal
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            queryClient.invalidateQueries({ queryKey: ['field-users'] });
          }}
        />
      )}
      {cardFor && (
        <CredentialCard
          tenantName={useAuthStore.getState().tenant?.tenantName ?? ''}
          fullName={cardFor.full_name}
          employeeId={cardFor.employee_id}
          code={cardFor.code}
          expiresAt={cardFor.expires_at}
          shareUrl={cardUrl(cardFor.employee_id)}
          onClose={() => setCardFor(null)}
        />
      )}
      {selected && <FieldUserDetailPanel user={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

const MENU_WIDTH = 144;
const MENU_HEIGHT = 44;
const VIEWPORT_GUTTER = 8;

/**
 * Row action menu, rendered into a portal on `document.body`.
 *
 * The table lives inside `.data-table-wrapper { overflow: hidden }` nested in
 * `.data-table-scroll { overflow-x: auto }`. An absolutely-positioned menu is
 * clipped by both — and with a single row there's no table height to scroll
 * into, so "Delete" was unreachable. Portalling out of the table and
 * positioning `fixed` against the trigger's viewport rect sidesteps the
 * clipping entirely, at any row count.
 */
function RowMenu({ onDelete }: { onDelete: () => void }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;
      // Flip above the trigger when there isn't room below.
      const below = rect.bottom + 4;
      const flip = below + MENU_HEIGHT > window.innerHeight - VIEWPORT_GUTTER;
      setPos({
        top: flip ? Math.max(VIEWPORT_GUTTER, rect.top - MENU_HEIGHT - 4) : below,
        left: Math.max(VIEWPORT_GUTTER, rect.right - MENU_WIDTH),
      });
    };
    place();
    // A fixed menu doesn't travel with its trigger — close instead of drift.
    const dismiss = () => setOpen(false);
    window.addEventListener('resize', dismiss);
    window.addEventListener('scroll', dismiss, true);
    return () => {
      window.removeEventListener('resize', dismiss);
      window.removeEventListener('scroll', dismiss, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => setOpen((o) => !o)}
        className="btn-ghost btn-sm"
        aria-label="More actions"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <MoreHorizontal className="h-4 w-4" />
      </button>
      {open &&
        pos &&
        createPortal(
          <>
            <div className="fixed inset-0 z-[60]" onClick={() => setOpen(false)} />
            <div
              role="menu"
              style={{ top: pos.top, left: pos.left, width: MENU_WIDTH }}
              className="fixed z-[61] overflow-hidden rounded-md border border-border bg-elevated py-1 shadow-elevated"
            >
              <button
                role="menuitem"
                onClick={() => {
                  setOpen(false);
                  onDelete();
                }}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm text-danger hover:bg-muted"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
            </div>
          </>,
          document.body,
        )}
    </>
  );
}

function AddFieldUserModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [employeeId, setEmployeeId] = useState('');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () =>
      fieldUserApi.create({
        employee_id: employeeId,
        full_name: fullName,
        phone: phone || undefined,
      }),
    onSuccess: onCreated,
    onError: (e: Error) => setError(e.message),
  });

  return (
    <div className="modal-overlay">
      <div className="modal">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <Plus className="h-5 w-5 text-primary" />
          Add field user
        </h2>
        <form
          className="mt-4 space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate();
          }}
        >
          <div>
            <label className="block text-sm font-medium">Employee ID</label>
            <input
              required
              className="input mt-1 font-mono"
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Full name</label>
            <input
              required
              className="input mt-1"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-sm font-medium">Phone (optional)</label>
            <input
              className="input mt-1"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          </div>
          {error && (
            <p
              className="flex items-start gap-2 rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger"
              role="alert"
            >
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>{error}</span>
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" disabled={mutation.isPending} className="btn-primary">
              {mutation.isPending ? 'Creating…' : 'Add field user'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
